/**
 * All shell copy in one place (docs/ASSETS.md §5). The "Concept prototype" footer and the "Simulated voice" /
 * "Presenter mode" banners must stay visible — they keep the prototype honest about what it is.
 */
export const strings = {
  footer: {
    conceptPrototype: 'Concept prototype',
  },
  voice: {
    simulatedBadge: 'Simulated voice',
    /** Shown whenever the active voice client is the mock (T6, docs/ARCHITECTURE.md §9). */
    simulatedNoProvider: 'Simulated voice: no provider configured',
    simulatedPresenterMode: 'Simulated voice: presenter mode',
    simulatedActivityStrip: 'Simulated voice activity',
    modeRemote: 'Remote',
    modeRemoteHint: 'Ask for the microphone with a button press; nothing turns on automatically.',
    modeInPerson: 'In person',
    modeInPersonHint: 'Microphones start muted to avoid feedback in a shared room.',
    modeOff: 'Off',
    modeOffHint: 'No voice for this room.',
    panelTitle: 'Voice',
    statusIdle: 'Idle',
    statusConnecting: 'Connecting…',
    statusConnected: 'Connected',
    statusListening: 'Listening',
    statusReconnecting: 'Reconnecting…',
    statusPermissionDenied: 'Permission denied',
    statusDeviceError: 'Device error',
    statusFailed: 'Voice unavailable',
    statusDisconnected: 'Disconnected',
    permissionDeniedHint: 'Allow the microphone for this site in your browser (the lock or camera icon in the address bar), then retry.',
    deviceErrorHint: 'Check that a microphone is connected and not already in use by another app, then retry.',
    retry: 'Retry voice',
    mute: 'Mute',
    unmute: 'Unmute',
    enableMicrophone: 'Enable microphone',
    joinMicrophone: 'Join with microphone',
    joinMicrophoneHint: 'You can already hear the room. Joining with microphone asks your browser for permission.',
    enableHuddleAudio: 'Enable huddle audio',
    listeningOnly: 'Listening only',
    speaking: 'Speaking',
    muted: 'Muted',
    participantsTitle: 'On voice',
  },
  presenter: {
    banner: 'Presenter mode: simulated participants',
    simulatedTag: 'Simulated',
    missing: 'Presenter view is not available in this build.',
    noCredentials: 'This presenter session is not open in this browser. Start a new one.',
    startNew: 'Start a presenter session',
    loading: 'Loading presenter session…',
    perspectiveTitle: 'Perspective',
    perspectiveOverview: 'Overview (spectator)',
    perspectiveViewingOverview: 'Viewing as: overview (spectator)',
    perspectiveViewing: (name: string) => `Viewing as: ${name} (simulated)`,
    controlsTitle: 'Presenter controls',
    pauseSession: 'Pause session',
    resumeSession: 'Resume session',
    resetFixture: 'Reset fixture',
    resetConfirm: 'Reset this fixture? It restarts from the beginning with the same seed.',
    replaySeed: 'Replay (same seed)',
    quickActionsTitle: 'Quick actions',
    quickActionsNone: 'No quick actions for this activity yet — switch perspective to act as a participant directly.',
    allSuggest: 'All sims suggest (deterministic)',
    deciderCommits: 'Decider commits',
    rosterTitle: 'Simulated participants',
    removeSim: 'Remove',
    logTitle: 'Action log',
    logEmpty: 'No actions sent yet.',
  },
  presenterEntry: {
    title: 'Presenter mode',
    description:
      'Run a deterministic fixture with simulated participants: switch perspectives, operate the real module controls on their behalf, pause, and replay from the same seed.',
    fixtureLabel: 'Fixture',
    fixtureLoading: 'Loading fixtures…',
    fixtureNone: 'No fixtures are available in this build.',
    seedLabel: 'Seed',
    seedHint: 'The same seed replays identically (dice, generated fragments).',
    seedRandomize: 'Randomize',
    presenterNameLabel: 'Presenter name',
    presenterKeyLabel: 'Presenter key (only if this server requires one)',
    submit: 'Start presenting',
    submitting: 'Starting…',
  },
  connection: {
    connecting: 'Connecting…',
    connected: 'Connected',
    reconnecting: 'Reconnecting…',
    closed: 'Disconnected',
    closedSuperseded: 'This session is open in another tab.',
    closedRemoved: 'You were removed from this session.',
    closedLeft: 'You left the session.',
    closedSessionEnded: 'This session has ended.',
    closedUnauthenticated: 'Your session credentials were not accepted.',
    closedExpired: 'This session credential has expired.',
    closedServerShutdown: 'The server is restarting. Reload to reconnect.',
  },
  home: {
    title: 'Momentum prototype',
    subtitle: 'A small collection of concept experiences.',
    momentumCardTitle: 'Momentum',
    momentumCardDescription: 'A shared board, mini activities, and a lobby for up to six players.',
  },
  entry: {
    title: 'Momentum',
    description:
      'Gather a small group around a shared board, play short activities together, and see the board pick up where you left off. This is an infrastructure prototype: the rules are placeholders, the shell is real.',
    createRoom: 'Create room',
    joinWithCode: 'Join with code',
  },
  create: {
    title: 'Create a room',
    displayNameLabel: 'Your display name',
    displayNamePlaceholder: 'e.g. Alex',
    characterLabel: 'Pick a character',
    hostIsPlayerLabel: 'I want to play too',
    hostIsPlayerHint: 'Turn this off to host and facilitate without taking a player slot.',
    voiceModeLabel: 'Voice mode',
    submit: 'Create room',
    submitting: 'Creating…',
  },
  join: {
    title: 'Join a room',
    displayNameLabel: 'Your display name',
    displayNamePlaceholder: 'e.g. Sam',
    characterLabel: 'Pick a character',
    roleLabel: 'Join as',
    rolePlayer: 'Player',
    roleSpectator: 'Spectator (read-only)',
    submit: 'Join',
    submitting: 'Joining…',
    lateJoinSpectator: 'This room is already playing. You can join as a spectator.',
    lateJoinWait: 'This room is already playing. You will wait in the lobby until the host returns everyone there.',
    lateJoinReject: 'This room is already playing and is not accepting new joins right now.',
    lobbyFull: 'This room is full.',
    ended: 'This session has ended.',
    invalidInvite: 'This invite link is no longer valid.',
  },
  code: {
    title: 'Join with a code',
    codeLabel: 'Room code',
    codePlaceholder: 'ABCD12',
    submit: 'Find room',
    submitting: 'Looking…',
    notFound: "That code didn't match a room.",
  },
  lobby: {
    title: 'Lobby',
    participants: 'Participants',
    you: 'You',
    host: 'Host',
    ready: 'Ready',
    notReady: 'Not ready',
    simulated: 'Simulated',
    disconnected: 'Disconnected',
    muted: 'Muted',
    readyToggleOn: 'Mark not ready',
    readyToggleOff: "I'm ready",
    inviteTitle: 'Invite others',
    inviteLink: 'Invite link',
    inviteCode: 'Or share this code',
    inviteLocalhostCaveat: 'Localhost links are not phone-ready. Use this on the same machine only.',
    inviteRotate: 'Rotate invite',
    inviteRotating: 'Rotating…',
    invitePersistent: 'Invite players',
    inviteShareTitle: 'Join my Momentum room',
    inviteShare: 'Share invite',
    inviteCopy: 'Copy invite',
    inviteCopied: 'Copied',
    inviteCopyFailed: 'Could not copy the invite. Select the link above to copy it.',
    characterLabel: 'Character',
    emotesTitle: 'Say hello',
    emoteSlowDown: "Slow down — you're emoting too fast.",
    launchTitle: 'Start an activity',
    launchModule: 'Module',
    launchParticipants: 'Participants',
    launchTeams: 'Teams',
    launchButton: 'Launch',
    launchLaunching: 'Launching…',
    launchNoModules: 'No launchable activities are available.',
    meetingDemoLabel: 'Momentum meeting demo',
    meetingDemoHint: 'Play a full board round, meet in a shared activity, then return to the map for the next round.',
    leave: 'Leave',
    waitingForHost: 'Waiting for the host to launch an activity.',
    characterSlotTaken: 'Taken',
    characterSlotFree: 'Free',
  },
  play: {
    title: 'Playing',
    boardPreserved: 'Board preserved',
    boardPreservedHint: 'Positions are kept while this activity runs.',
    noRenderer: (rendererId: string) => `No renderer for ${rendererId}`,
    pauseTitle: 'Paused',
    pauseWaitingFor: 'Waiting for',
    resume: 'Resume',
    removeParticipant: 'Remove participant',
    abandonActivity: 'Abandon activity',
    endSession: 'End session',
    hostControls: 'Host controls',
    reasonHostPaused: 'The host paused the session.',
    reasonPresenterPaused: 'The presenter paused the session.',
    reasonParticipantDisconnected: 'A participant disconnected.',
    reasonServerRestart: 'The server restarted and needs the host to resume.',
    reasonModuleRequested: 'The activity requested a pause.',
  },
  results: {
    title: 'Results',
    historyCount: (count: number) => `${count} earlier round${count === 1 ? '' : 's'} in this room`,
    returnToLobby: 'Return to lobby',
    returningToLobby: 'Returning…',
    leave: 'Leave',
    statusCompleted: 'Completed',
    statusAbandoned: 'Abandoned',
    noSummary: 'No summary provided.',
  },
  session: {
    noCredentials: "You don't have credentials for this session yet.",
    noCredentialsWithInvite: 'Join this session to continue.',
    joinLink: 'Go to the invite',
    loading: 'Loading session…',
  },
  /** Board -> mini-game transition (docs/concept/board/06-minimal-transition.png). Every game-specific string
   * here (title, format, goal/play/score copy) is illustrative fallback text only — the real strings always
   * come from the caller as data, never from this file, per the brief. */
  transition: {
    nextUp: 'Next up',
    vs: 'VS',
    you: 'You',
    goal: 'Goal',
    play: 'Play',
    score: 'Score',
    ready: 'Ready',
    readyPending: 'Marking ready…',
    readyCount: (ready: number, total: number) => `${ready} / ${total} ready`,
    youAreReady: "You're ready",
    playerReady: (name: string) => `${name} is ready`,
    playerLoading: (name: string) => `${name} is loading…`,
    playerDisconnected: (name: string) => `${name} disconnected`,
    startingIn: (seconds: number) => `Starting in ${seconds}`,
    startingNow: 'Starting now',
    startingTogether: 'Starting together',
    mapButton: 'Map',
    mapUnavailable: 'Map is unavailable during this transition',
    crystals: (collected: number, total: number) => `${collected} of ${total} crystals collected`,
    regionLabel: 'Mini-game transition',
    disconnectNotice: (name: string) => `${name} disconnected — waiting for them to return.`,
  },
  display: {
    title: 'Spectator display',
    joinPrompt: 'Join as a spectator to watch this session.',
    joinAsSpectator: 'Join as spectator',
  },
  common: {
    error: 'Something went wrong',
    retry: 'Try again',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
  },
} as const;
